import cn from 'classnames';
import Image from '@packages/shared/core/common/components/common/Image';
import { Expert } from '@packages/shared/core/experts/entities';
import Button from '@packages/shared/core/common/components/common/Button';
import ExpertAbout from '@packages/shared/core/common/components/common/ExpertCard/ExpertAbout';
import { DEFAULT_FREE_MINUTES } from '@packages/shared/core/common/constants/appDefaults';
import Rating from '@packages/shared/core/common/components/common/Rating';
import ExpertStatusIndicator from '@packages/shared/core/common/components/common/ExpertStatusIndicator';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import ExpertCardReviews from '../ExpertCardReviews';
import ExpertCardConsultations from '../ExpertCardConsultations';
import ExpertCardExperience from '../ExpertCardExperience';
import ExpertCardSkills from '../ExpertCardSkills';
import styles from './ExpertCardHorizontal.module.scss';

export type ExpertCardHorizontalProps = {
  className?: string;
  onStartChat: () => void;
  expert: Expert;
  expertSkillsLimit?: number;
};

const ExpertCardHorizontal = ({
  className = '',
  onStartChat,
  expert,
  expertSkillsLimit = 20,
}: ExpertCardHorizontalProps) => {
  const rootClasses = cn({
    [styles.root]: true,
    [styles.rootBestMatch]: expert.isBestMatch,
    [className]: !!className,
  });

  const skillsClasses = cn({
    [styles.skills]: true,
    [styles.skillsBestMatch]: expert.isBestMatch,
  });

  const descriptionClasses = cn({
    [styles.description]: true,
    [styles.descriptionBestMatch]: expert.isBestMatch,
  });

  const buttonClasses = cn({
    [styles.button]: true,
    [styles.buttonBestMatch]: expert.isBestMatch,
  });

  return (
    <div className={rootClasses}>
      {expert.isBestMatch && <span className={styles.badge}>Your Best Match</span>}

      <div className={styles.personalWrapper}>
        <div className={styles.imageWrapper}>
          <Image
            type="future"
            className={styles.expertImage}
            src={expert.image}
            alt={expert.name}
            width={150}
            height={150}
          />

          <ExpertStatusIndicator status={expert.status} className={styles.status} />
        </div>

        <div>
          <p className={styles.name}>{expert.name}</p>

          <ExpertAbout
            descriptionProps={{
              text: expert.description,
              className: descriptionClasses,
            }}
          />
        </div>
      </div>

      <div className={styles.infoWrapper}>
        <ul className={styles.infoList}>
          <li className={styles.infoListItem}>
            <ExpertCardReviews
              expert={expert}
              icon={<Rating value={expert.rating} className={styles.stars} precision={0.2} readOnly size="small" />}
            />
          </li>

          <li className={styles.infoListItem}>
            <ExpertCardConsultations consultations={expert.total_orders} />
          </li>

          <li className={styles.infoListItem}>
            <ExpertCardExperience experience={expert.experience} />
          </li>
        </ul>

        <ExpertCardSkills
          className={skillsClasses}
          specializations={expert.specializations}
          skillsLimit={expertSkillsLimit}
        />
      </div>

      <div>
        <p className={styles.discount}>free {DEFAULT_FREE_MINUTES} minutes</p>

        <Button
          wrapperClassName={styles.buttonWrapper}
          className={buttonClasses}
          onClick={onStartChat}
          data-testid={DataTestId.START_CHAT_BUTTON}
        >
          Start Free Chat
        </Button>
      </div>
    </div>
  );
};

export default ExpertCardHorizontal;
